import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CalendarDays,
  Download,
  FileCheck2,
  LoaderCircle,
  MessageSquareText,
  UsersRound,
} from "lucide-react";

import { Button } from "@/design-system/primitives";
import { useClinicianReport } from "@/lib/api/clinicianHooks";
import type {
  ClinicianReportResponse,
  ClinicianReportType,
} from "@/types/clinician";
import { OwnerOptions, useOwnerFilter } from "./ownerFilter";

const REPORTS: Record<
  ClinicianReportType,
  { title: string; description: string; icon: typeof FileCheck2 }
> = {
  prescriptions: {
    title: "Prescriptions",
    description:
      "Authored prescriptions across Healthie and the clinician portal, plus clinical rejections. Orders, payments, and fills are not counted as scripts.",
    icon: FileCheck2,
  },
  prescribers: {
    title: "Weekly by prescriber",
    description:
      "Each prescriber's week: scripts written (initial and refill), rejections, and how many approvals followed a live visit (sync) or the intake alone (async).",
    icon: UsersRound,
  },
  visits: {
    title: "Visits",
    description: "Sync requirements, scheduled visits, and Zoom attendance.",
    icon: CalendarDays,
  },
  inbox: {
    title: "Clinical inbox",
    description: "New work, resolutions, outstanding load, and response time.",
    icon: MessageSquareText,
  },
};

const METRIC_LABELS: Record<string, string> = {
  total: "Total decisions",
  accepted: "Prescriptions written",
  initial: "Initial Rx",
  refill: "Refill Rx",
  rejected: "Rejected",
  scripts: "Scripts written",
  sync: "Sync",
  async: "Async",
  prescribers: "Prescribers",
  sync_required: "Sync visits required",
  scheduled: "Visits scheduled",
  attended: "Attended",
  physician_missed: "Missed · clinician",
  attendee_missed: "Missed · patient",
  new_conversations: "New conversations",
  resolved: "Resolved",
  resolved_by_ai: "Resolved · AI",
  resolved_by_human: "Resolved · human",
  resolved_other: "Resolved · other",
  outstanding: "Outstanding",
  average_resolution_hours: "Avg resolution · hours",
};

const PRESCRIPTION_SOURCE_LABELS: Record<string, string> = {
  healthie: "Healthie",
  internal_portal: "Internal Portal",
  healthie_direct: "Healthie",
  clinician_portal: "Internal Portal",
  portal_decision: "Internal Portal",
};

type Preset = "this_month" | "last_month" | "last_week" | "custom";

function localDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function presetRange(preset: Exclude<Preset, "custom">) {
  const now = new Date();
  if (preset === "this_month") {
    return {
      start: localDate(new Date(now.getFullYear(), now.getMonth(), 1)),
      end: localDate(now),
    };
  }
  if (preset === "last_month") {
    return {
      start: localDate(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
      end: localDate(new Date(now.getFullYear(), now.getMonth(), 0)),
    };
  }
  const day = now.getDay() || 7;
  const lastSunday = new Date(now);
  lastSunday.setDate(now.getDate() - day);
  const lastMonday = new Date(lastSunday);
  lastMonday.setDate(lastSunday.getDate() - 6);
  return { start: localDate(lastMonday), end: localDate(lastSunday) };
}

function reportType(value: string | undefined): ClinicianReportType | null {
  return value === "prescriptions" ||
    value === "prescribers" ||
    value === "visits" ||
    value === "inbox"
    ? value
    : null;
}

export function ReportingScreen() {
  const { reportType: reportParam } = useParams();
  const activeReport = reportType(reportParam);
  if (!activeReport) return <ReportLanding />;
  return <ReportDetail reportType={activeReport} />;
}

function ReportLanding() {
  return (
    <div className="max-w-[92rem] mx-auto px-12 md:px-24 xl:px-40 py-24 md:py-40 flex flex-col gap-20">
      <header>
        <p className="type-label-sm text-ink-muted">CLINICAL REPORTING</p>
        <h1 className="type-heading-xl mt-4">Reports</h1>
        <p className="type-body-sm text-ink-muted mt-6 max-w-[58rem]">
          Choose a report, set the date range, and export the filtered rows to
          CSV. Historical reads run only when you open a report.
        </p>
      </header>
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-12">
        {(
          Object.entries(REPORTS) as Array<
            [ClinicianReportType, (typeof REPORTS)[ClinicianReportType]]
          >
        ).map(([type, report]) => {
          const Icon = report.icon;
          return (
            <Link
              key={type}
              to={`/clinician-portal/reports/${type}`}
              className="border border-line-subtle bg-raised p-18 min-h-[17rem] flex flex-col gap-10 hover:border-ink focus-visible:outline-none focus-visible:shadow-focus"
            >
              <Icon size={22} aria-hidden />
              <div>
                <h2 className="type-heading-md">{report.title}</h2>
                <p className="type-body-sm text-ink-muted mt-5">
                  {report.description}
                </p>
              </div>
              <span className="type-label-sm mt-auto">Open report →</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function ReportDetail({ reportType }: { reportType: ClinicianReportType }) {
  const initial = presetRange("this_month");
  const [preset, setPreset] = useState<Preset>("this_month");
  const [startDate, setStartDate] = useState(initial.start);
  const [endDate, setEndDate] = useState(initial.end);
  // Supervisors who prescribe start on their own patients (team routing).
  const owner = useOwnerFilter();
  const supervisor = owner.supervisor;
  const report = useClinicianReport(
    reportType,
    startDate,
    endDate,
    owner.clinicianId,
    owner.ready,
  );
  const columns = useMemo(() => columnsForReport(reportType), [reportType]);
  const config = REPORTS[reportType];

  const selectPreset = (next: Preset) => {
    setPreset(next);
    if (next === "custom") return;
    const range = presetRange(next);
    setStartDate(range.start);
    setEndDate(range.end);
  };

  return (
    <div className="max-w-[112rem] mx-auto px-12 md:px-24 xl:px-40 py-24 md:py-40 flex flex-col gap-18">
      <header className="flex flex-wrap items-end justify-between gap-12">
        <div>
          <Link
            to="/clinician-portal/reports"
            className="type-label-sm text-ink-muted inline-flex items-center gap-5"
          >
            <ArrowLeft size={13} aria-hidden /> Reports
          </Link>
          <h1 className="type-heading-xl mt-8">{config.title}</h1>
          <p className="type-body-sm text-ink-muted mt-5">
            {config.description}
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={!report.data?.rows.length}
          onClick={() => report.data && exportReportCsv(report.data, columns)}
        >
          <Download size={13} aria-hidden /> Export CSV
        </Button>
      </header>

      <div className="border border-line-subtle bg-raised p-12 flex flex-wrap items-end gap-10">
        <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
          RANGE
          <select
            value={preset}
            onChange={(event) => selectPreset(event.target.value as Preset)}
            className="min-h-40 border border-line bg-page px-10 type-body-sm text-ink"
          >
            <option value="this_month">This month</option>
            <option value="last_month">Last month</option>
            <option value="last_week">Last week</option>
            <option value="custom">Custom</option>
          </select>
        </label>
        <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
          FROM
          <input
            type="date"
            value={startDate}
            onChange={(event) => {
              setPreset("custom");
              setStartDate(event.target.value);
            }}
            className="min-h-40 border border-line bg-page px-10 type-body-sm text-ink"
          />
        </label>
        <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
          THROUGH
          <input
            type="date"
            value={endDate}
            onChange={(event) => {
              setPreset("custom");
              setEndDate(event.target.value);
            }}
            className="min-h-40 border border-line bg-page px-10 type-body-sm text-ink"
          />
        </label>
        {supervisor && (
          <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
            CLINICIAN
            <select
              value={owner.selected}
              onChange={(event) => owner.select(event.target.value)}
              className="min-h-40 border border-line bg-page px-10 type-body-sm text-ink"
            >
              <OwnerOptions owner={owner} />
            </select>
          </label>
        )}
      </div>

      {report.isLoading || !owner.ready ? (
        <div
          className="min-h-[18rem] border border-line-subtle bg-raised flex flex-col items-center justify-center gap-8 text-center p-20"
          role="status"
          aria-live="polite"
        >
          <LoaderCircle
            className="animate-spin text-ink-muted"
            size={24}
            aria-hidden
          />
          <div>
            <p className="type-heading-sm">
              Loading {config.title.toLowerCase()}
            </p>
            <p className="type-body-sm text-ink-muted mt-4">
              {reportType === "prescriptions"
                ? "Reading authored prescriptions and clinical rejections for this range."
                : "Reading the clinical history for this date range."}
            </p>
          </div>
        </div>
      ) : report.isError || !report.data ? (
        <div className="border border-danger/30 bg-danger-wash p-16 type-body-sm text-danger">
          This report could not be loaded. Nothing was changed. Check the date
          range and retry.
        </div>
      ) : (
        <>
          {report.isFetching && (
            <div
              className="border border-line-subtle bg-sunken px-10 py-7 type-label-xs text-ink-muted inline-flex items-center gap-6"
              role="status"
            >
              <LoaderCircle className="animate-spin" size={13} aria-hidden />
              Updating report…
            </div>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-5 border border-line-subtle bg-raised divide-x divide-y lg:divide-y-0 divide-line-subtle">
            {Object.entries(report.data.metrics).map(([key, value]) => (
              <div key={key} className="p-12 flex flex-col gap-3">
                <span className="type-label-xs text-ink-muted">
                  {METRIC_LABELS[key] ?? key.replaceAll("_", " ")}
                </span>
                <span className="type-heading-lg tabular-nums">
                  {value == null ? "—" : value}
                </span>
              </div>
            ))}
          </div>
          <ReportTable
            reportType={reportType}
            rows={report.data.rows}
            columns={columns}
          />
          {report.data.notes.length > 0 && (
            <div className="border border-line-subtle bg-sunken p-10 text-11 text-ink-muted">
              {report.data.notes.map((note) => (
                <p key={note}>{note}</p>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

interface ReportColumn {
  key: string;
  label: string;
}

function columnsForReport(reportType: ClinicianReportType): ReportColumn[] {
  if (reportType === "prescriptions") {
    return [
      { key: "occurred_at", label: "Date" },
      { key: "patient_name", label: "Patient" },
      { key: "patient_email", label: "Email" },
      { key: "order_number", label: "Related order" },
      { key: "product", label: "Product" },
      { key: "medication", label: "Medication" },
      { key: "outcome", label: "Decision" },
      { key: "prescription_type", label: "Type" },
      { key: "source", label: "Source" },
    ];
  }
  if (reportType === "prescribers") {
    return [
      { key: "week_start", label: "Week of" },
      { key: "prescriber", label: "Prescriber" },
      { key: "scripts", label: "Scripts" },
      { key: "initial", label: "Initial" },
      { key: "refill", label: "Refill" },
      { key: "rejected", label: "Rejected" },
      { key: "sync", label: "Sync" },
      { key: "async", label: "Async" },
      { key: "not_recorded", label: "Not recorded" },
    ];
  }
  if (reportType === "visits") {
    return [
      { key: "scheduled_at", label: "Scheduled" },
      { key: "patient_name", label: "Patient" },
      { key: "patient_email", label: "Email" },
      { key: "clinician_name", label: "Clinician" },
      { key: "appointment_type", label: "Visit type" },
      { key: "outcome", label: "Outcome" },
      { key: "participant_count", label: "Participants" },
    ];
  }
  return [
    { key: "waiting_since", label: "Opened" },
    { key: "patient_name", label: "Patient" },
    { key: "patient_email", label: "Email" },
    { key: "category", label: "Category" },
    { key: "capability", label: "Queue" },
    { key: "workflow_state", label: "State" },
    { key: "resolution_type", label: "Resolved by" },
    { key: "resolved_at", label: "Resolved" },
    { key: "preview", label: "Latest message" },
  ];
}

function displayValue(value: unknown, key: string) {
  if (value == null || value === "") return "—";
  if (key === "source") {
    return (
      PRESCRIPTION_SOURCE_LABELS[String(value)] ??
      String(value).replaceAll("_", " ")
    );
  }
  if (key === "prescription_type") {
    return String(value) === "refill" ? "Refill" : "Initial";
  }
  if (key.endsWith("_at")) {
    const timestamp = Date.parse(String(value));
    if (Number.isFinite(timestamp)) return new Date(timestamp).toLocaleString();
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value).replaceAll("_", " ");
}

function ReportTable({
  reportType,
  rows,
  columns,
}: {
  reportType: ClinicianReportType;
  rows: ClinicianReportResponse["rows"];
  columns: ReportColumn[];
}) {
  const pageSize = 100;
  const [page, setPage] = useState(0);
  useEffect(() => setPage(0), [reportType, rows]);
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const visiblePage = Math.min(page, pageCount - 1);
  const visibleRows = rows.slice(
    visiblePage * pageSize,
    (visiblePage + 1) * pageSize,
  );
  if (!rows.length) {
    return (
      <div className="border border-line-subtle bg-raised p-32 text-center">
        <p className="type-heading-sm">No rows in this date range</p>
        <p className="type-body-sm text-ink-muted mt-5">
          Choose another range or clinician.
        </p>
      </div>
    );
  }
  return (
    <div className="border border-line-subtle bg-raised">
      <div className="px-10 py-8 border-b border-line-subtle flex flex-wrap items-center justify-between gap-8 type-label-xs text-ink-muted">
        <span>
          SHOWING {visiblePage * pageSize + 1}–
          {Math.min((visiblePage + 1) * pageSize, rows.length)} OF {rows.length}
          {rows.length > pageSize ? " · CSV EXPORT INCLUDES ALL ROWS" : ""}
        </span>
        {pageCount > 1 && (
          <div className="flex items-center gap-6">
            <button
              type="button"
              className="border border-line px-8 py-5 disabled:opacity-40"
              disabled={visiblePage === 0}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
            >
              ← Previous
            </button>
            <span>
              PAGE {visiblePage + 1} / {pageCount}
            </span>
            <button
              type="button"
              className="border border-line px-8 py-5 disabled:opacity-40"
              disabled={visiblePage >= pageCount - 1}
              onClick={() =>
                setPage((current) => Math.min(pageCount - 1, current + 1))
              }
            >
              Next →
            </button>
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[72rem] border-collapse">
          <thead className="bg-sunken">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className="px-10 py-8 text-left type-label-xs text-ink-muted border-b border-line-subtle"
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-line-subtle">
            {visibleRows.map((row) => (
              <tr key={row.id} className="hover:bg-sunken/60">
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className="px-10 py-9 type-body-sm align-top max-w-[24rem]"
                  >
                    {column.key === "patient_name" && row.patient_id ? (
                      <Link
                        to={`/clinician-portal/patients/${String(row.patient_id)}`}
                        className="underline underline-offset-3"
                      >
                        {displayValue(row[column.key], column.key)}
                      </Link>
                    ) : reportType === "inbox" && column.key === "preview" ? (
                      <Link
                        to={`/clinician-portal/inbox/${row.id}`}
                        className="line-clamp-2 underline underline-offset-3"
                      >
                        {displayValue(row[column.key], column.key)}
                      </Link>
                    ) : (
                      <span className="line-clamp-3">
                        {displayValue(row[column.key], column.key)}
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function csvCell(value: unknown) {
  const raw = value == null ? "" : String(value);
  // Patient-authored text may begin with a spreadsheet formula marker.
  // Preserve the visible value while forcing Excel/Sheets to treat it as text.
  const text = /^[=+\-@]/.test(raw) ? `'${raw}` : raw;
  return `"${text.replaceAll('"', '""')}"`;
}

function exportReportCsv(
  report: ClinicianReportResponse,
  columns: ReportColumn[],
) {
  const lines = [
    columns.map((column) => csvCell(column.label)).join(","),
    ...report.rows.map((row) =>
      columns.map((column) => csvCell(row[column.key])).join(","),
    ),
  ];
  const blob = new Blob([lines.join("\r\n")], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `clinical-${report.report_type}-${report.start_date}-to-${report.end_date}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}
